// frontend/src/components/personalizer/GoogleWalletPreview.jsx
// Google Wallet-style pass preview for the card designer.
// Mimics the real Google Wallet loyalty card layout.

import { QRCodeSVG } from 'qrcode.react';
import { useLanguage } from '../../utils/i18n';
import { normalizeLogoUrl } from '../../utils/urlHelper';

function isDark(hex) {
    if (!hex) return false;
    const c = hex.replace('#', '');
    const r = parseInt(c.substring(0, 2), 16);
    const g = parseInt(c.substring(2, 4), 16);
    const b = parseInt(c.substring(4, 6), 16);
    return ((r * 299) + (g * 587) + (b * 114)) / 1000 < 128;
}

/** Darken a hex colour to create the native wallet top section tint */
function darkenColor(hex, amount = 20) {
    const c = hex.replace('#', '');
    const r = Math.max(0, parseInt(c.substring(0, 2), 16) - amount);
    const g = Math.max(0, parseInt(c.substring(2, 4), 16) - amount);
    const b = Math.max(0, parseInt(c.substring(4, 6), 16) - amount);
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

export default function GoogleWalletPreview({
    bgColor = '#1a73e8',
    textColor: textColorProp = null,
    stampIcon = '⭐',
    stampGoal = 10,
    currentCount = 0,
    rewardText = 'Free reward!',
    cardTitle = 'Loyalty Card',
    logoUrl = null,
    clientName = 'John Doe',
    stampColor = 'rgba(255,255,255,0.25)',
    numberColor = 'rgba(255,255,255,0.3)',
    headerMode = 'title',
}) {
    const { t } = useLanguage();
    const dark = isDark(bgColor);
    const textColor = textColorProp || (dark ? '#ffffff' : '#000000');
    const mutedColor = dark ? 'rgba(255,255,255,0.55)' : 'rgba(0,0,0,0.45)';
    const circleBorder = dark ? 'rgba(255,255,255,0.35)' : 'rgba(0,0,0,0.15)';
    const cols = 5;

    // Use a darker shade for the top native section
    const topBg = darkenColor(bgColor, 30);

    // Normalize logo URL
    const normalizedLogo = normalizeLogoUrl(logoUrl);

    return (
        <div style={{
            width: '100%',
            maxWidth: 340,
            borderRadius: 20,
            overflow: 'hidden',
            boxShadow: '0 12px 40px -10px rgba(0,0,0,0.5)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Helvetica Neue", sans-serif',
        }}>
            {/* ═══ TOP SECTION: Card face (darkened bgColor) ═══ */}
            <div style={{ background: topBg, padding: '16px 24px 0' }}>
                {/* Header: Logo circle + Business name */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 16,
                }}>
                    {normalizedLogo && (
                        <div key={normalizedLogo} style={{
                            width: 32, height: 32, borderRadius: '50%',
                            overflow: 'hidden', flexShrink: 0,
                            border: `2px solid ${dark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.1)'}`,
                            background: dark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <img
                                src={normalizedLogo}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                onError={(e) => { e.target.parentElement.style.display = 'none'; }}
                            />
                        </div>
                    )}
                    <span style={{
                        fontSize: 18,
                        fontWeight: 600,
                        color: textColor,
                    }}>
                        {cardTitle}
                    </span>
                </div>

                {/* QR Code */}
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    padding: '4px 0 24px',
                }}>
                    <div style={{
                        background: '#ffffff',
                        borderRadius: 16,
                        padding: 16,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    }}>
                        <QRCodeSVG
                            value={`${window.location.origin}/scan-preview`}
                            size={140}
                            level="H"
                            includeMargin={false}
                        />
                    </div>
                </div>
            </div>

            {/* ═══ BOTTOM SECTION: Stamp card details (shifted bg) ═══ */}
            <div style={{
                background: bgColor,
                padding: '16px 24px 20px',
            }}>
                {/* Logo + Business name + Client */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 12,
                }}>
                    <div>
                        <p style={{
                            margin: 0,
                            fontSize: 15,
                            fontWeight: 700,
                            color: textColor,
                        }}>
                            {cardTitle}
                        </p>
                        <p style={{
                            margin: '1px 0 0',
                            fontSize: 12,
                            color: mutedColor,
                            fontWeight: 500,
                        }}>
                            {clientName}
                        </p>
                    </div>
                </div>

                {/* Stamp grid - Centered flexbox */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: 8,
                    marginBottom: 16,
                }}>
                    {Array.from({ length: stampGoal }).map((_, i) => {
                        const filled = i < currentCount;
                        return (
                            <div
                                key={i}
                                style={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: '50%',
                                    background: filled ? stampColor : 'transparent',
                                    border: `2px solid ${filled ? stampColor : circleBorder}`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: filled ? 22 : 14,
                                    fontWeight: 500,
                                    color: filled ? 'inherit' : (numberColor || mutedColor),
                                }}
                            >
                                {filled ? stampIcon : i + 1}
                            </div>
                        );
                    })}
                </div>

                {/* Progress + reward */}
                <div style={{ textAlign: 'center' }}>
                    <p style={{
                        margin: 0,
                        fontSize: 14,
                        color: mutedColor,
                        fontWeight: 500,
                    }}>
                        {currentCount} / {stampGoal} {t('stamps_suffix')}
                    </p>
                    <p style={{
                        margin: '4px 0 0',
                        fontSize: 17,
                        fontWeight: 700,
                        color: textColor,
                    }}>
                        🎁 {rewardText}
                    </p>
                </div>
            </div>
        </div>
    );
}
